import React, { useEffect, useMemo, useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from '@/components/ui/toast';
import { cn } from '@/lib/utils';
import { useUser } from '@/store/useUser';
import {
  getMatchedCellClassName,
  getMatchHighlightState,
  getParticipantName,
  type ParticipantMergeHighlightParticipant,
} from './ParticipantMergePage.helpers';
import {
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  GitMerge,
  Search,
  ShieldAlert,
} from 'lucide-react';

const SEARCH_PARTICIPANT_MERGE_CANDIDATES = gql`
  query SearchParticipantMergeCandidates($input: ParticipantMergeSearchInput!) {
    searchParticipantMergeCandidates(input: $input) {
      totalCount
      page
      pageSize
      groups {
        groupId
        participantCount
        matchPercent
        scoreColor
        safetyLabel
        recommendedSurvivor
        matchedFields {
          field
          label
          value
          participantCount
        }
        participants {
          odrNumber
          firstName
          lastName
          displayName
          dob
          ssn
          cin
          cid
          champid
          fin
          bookings
          completeness
          matchPercent
          scoreColor
          recommendedKeep
          matchedFields
        }
      }
    }
  }
`;

const MERGE_PARTICIPANT_GROUPS = gql`
  mutation MergeParticipantGroups($selections: [ParticipantMergeSelectionInput!]) {
    mergeParticipantGroups(selections: $selections) {
      totalRequested
      totalSucceeded
      totalFailed
      results {
        message
        responseCode
        responseID
        extra
      }
    }
  }
`;

const DEFAULT_FILTERS = {
  matchOn: ['ssn', 'champid', 'dob'],
  name: '',
  dob: '',
  ssn: '',
  cin: '',
  cid: '',
  champid: '',
  fin: '',
  booking: '',
  fullNameSearch: false,
  pageSize: 25,
};

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const MATCH_FIELD_OPTIONS = [
  { value: 'name', label: 'Name' },
  { value: 'dob', label: 'DOB' },
  { value: 'ssn', label: 'SSN' },
  { value: 'cin', label: 'CIN' },
  { value: 'cid', label: 'CID' },
  { value: 'champid', label: 'ChampID' },
  { value: 'fin', label: 'FIN' },
  { value: 'booking', label: 'Booking' },
];

type MergeParticipant = ParticipantMergeHighlightParticipant & {
  completeness: number;
  matchPercent: number;
  scoreColor: string;
  recommendedKeep: boolean;
};

type MergeGroup = {
  groupId: string;
  participantCount: number;
  matchPercent: number;
  scoreColor: string;
  safetyLabel: string;
  recommendedSurvivor: string;
  matchedFields: Array<{
    field: string;
    label: string;
    value: string;
    participantCount: number;
  }>;
  participants: MergeParticipant[];
}

type SearchResult = {
  totalCount: number;
  page: number;
  pageSize: number;
  groups: MergeGroup[];
}

const buildFiltersFromSettings = (settings: any) => {
  if (!settings || typeof settings !== 'object') {
    return { ...DEFAULT_FILTERS, matchOn: [...DEFAULT_FILTERS.matchOn] };
  }

  return {
    matchOn: Array.isArray(settings.matchOn) && settings.matchOn.length ? [...settings.matchOn] : [...DEFAULT_FILTERS.matchOn],
    name: typeof settings.name === 'string' ? settings.name : '',
    dob: typeof settings.dob === 'string' ? settings.dob : '',
    ssn: typeof settings.ssn === 'string' ? settings.ssn : '',
    cin: typeof settings.cin === 'string' ? settings.cin : '',
    cid: typeof settings.cid === 'string' ? settings.cid : '',
    champid: typeof settings.champid === 'string' ? settings.champid : '',
    fin: typeof settings.fin === 'string' ? settings.fin : '',
    booking: typeof settings.booking === 'string' ? settings.booking : '',
    fullNameSearch: Boolean(settings.fullNameSearch),
    pageSize: typeof settings.pageSize === 'number' && settings.pageSize > 0 ? settings.pageSize : DEFAULT_FILTERS.pageSize,
  };
};

const getColorClasses = (scoreColor: string) => {
  if (scoreColor === 'green') {
    return {
      badge: 'border-green-200 bg-green-50 text-green-700',
      fill: 'bg-green-500',
      panel: 'border-green-200 bg-green-50/60',
    };
  }

  if (scoreColor === 'yellow') {
    return {
      badge: 'border-yellow-200 bg-yellow-50 text-yellow-700',
      fill: 'bg-yellow-500',
      panel: 'border-yellow-200 bg-yellow-50/60',
    };
  }

  return {
    badge: 'border-red-200 bg-red-50 text-red-700',
    fill: 'bg-red-500',
    panel: 'border-red-200 bg-red-50/60',
  };
};

const buildDefaultMergeTargets = (group: MergeGroup, participantToKeep: string) => {
  return group.participants
    .map((participant) => participant.odrNumber)
    .filter((odrNumber) => odrNumber !== participantToKeep);
};

const hasExpandedFilterValues = (filters: typeof DEFAULT_FILTERS) => {
  if (filters.name.trim()) {
    return true;
  }

  if (filters.dob.trim()) {
    return true;
  }

  if (filters.ssn.trim()) {
    return true;
  }

  if (filters.cin.trim()) {
    return true;
  }

  if (filters.cid.trim()) {
    return true;
  }

  if (filters.champid.trim()) {
    return true;
  }

  if (filters.fin.trim()) {
    return true;
  }

  if (filters.booking.trim()) {
    return true;
  }

  return filters.fullNameSearch;
};

const ScorePill = ({
  percent,
  scoreColor,
  label,
  compact = false,
}: {
  percent: number;
  scoreColor: string;
  label: string;
  compact?: boolean;
}) => {
  const classes = getColorClasses(scoreColor);

  if (compact) {
    return (
      <div className="min-w-[7rem]">
        <div className="flex items-center justify-between gap-2">
          <Badge variant="outline" className={cn('capitalize', classes.badge)}>
            {percent}%
          </Badge>
          <span className="text-xs uppercase tracking-wide text-[var(--neutral-gray-100)]">{label}</span>
        </div>
        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-[var(--neutral-gray-20)]">
          <div className={cn('h-full rounded-full transition-all', classes.fill)} style={{ width: `${percent}%` }} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-w-[13rem]">
      <div className="flex items-center justify-between gap-3">
        <Badge variant="outline" className={cn('capitalize', classes.badge)}>
          {percent}% {label}
        </Badge>
        <span className="text-xs text-[var(--neutral-gray-100)]">match confidence</span>
      </div>
      <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-[var(--neutral-gray-20)]">
        <div className={cn('h-full rounded-full transition-all', classes.fill)} style={{ width: `${percent}%` }} />
      </div>
    </div>
  );
};

export const ParticipantMergePage = () => {
  const { participantMergeSearchSettings, updateParticipantMergeSearchSettings } = useUser();
  const [filters, setFilters] = useState(() => buildFiltersFromSettings(participantMergeSearchSettings));
  const [submittedFilters, setSubmittedFilters] = useState(() => buildFiltersFromSettings(participantMergeSearchSettings));
  const [page, setPage] = useState(1);
  const [selectedGroups, setSelectedGroups] = useState<Record<string, boolean>>({});
  const [keepSelections, setKeepSelections] = useState<Record<string, string>>({});
  const [mergeSelections, setMergeSelections] = useState<Record<string, string[]>>({});
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const [filtersExpanded, setFiltersExpanded] = useState(() => hasExpandedFilterValues(buildFiltersFromSettings(participantMergeSearchSettings)));

  useEffect(() => {
    const nextFilters = buildFiltersFromSettings(participantMergeSearchSettings);
    setFilters(nextFilters);
    setSubmittedFilters(nextFilters);
    setFiltersExpanded(hasExpandedFilterValues(nextFilters));
  }, [participantMergeSearchSettings]);

  const queryInput = useMemo(() => {
    return {
      ...submittedFilters,
      page,
      pageSize: submittedFilters.pageSize,
    };
  }, [submittedFilters, page]);

  const { data, loading, error, refetch } = useQuery<{ searchParticipantMergeCandidates: SearchResult }>(
    SEARCH_PARTICIPANT_MERGE_CANDIDATES,
    {
      variables: { input: queryInput },
      fetchPolicy: 'network-only',
    }
  );

  const [mergeParticipantGroups, { loading: merging }] = useMutation(MERGE_PARTICIPANT_GROUPS);
  const groups = useMemo(() => data?.searchParticipantMergeCandidates?.groups ?? [], [data?.searchParticipantMergeCandidates?.groups]);
  const totalCount = data?.searchParticipantMergeCandidates?.totalCount ?? 0;
  const pageSize = submittedFilters.pageSize || DEFAULT_FILTERS.pageSize;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const visibleStart = totalCount ? ((page - 1) * pageSize) + 1 : 0;
  const visibleEnd = totalCount ? visibleStart + groups.length - 1 : 0;

  useEffect(() => {
    setKeepSelections((previous) => {
      const nextSelections = { ...previous };
      groups.forEach((group) => {
        if (nextSelections[group.groupId]) {
          return;
        }

        nextSelections[group.groupId] = group.recommendedSurvivor;
      });
      return nextSelections;
    });
  }, [groups]);

  useEffect(() => {
    setMergeSelections((previous) => {
      const nextSelections = { ...previous };

      groups.forEach((group) => {
        const participantToKeep = keepSelections[group.groupId] || group.recommendedSurvivor;
        const validParticipants = new Set(group.participants.map((participant) => participant.odrNumber));
        const existingSelections = Array.isArray(nextSelections[group.groupId])
          ? nextSelections[group.groupId].filter((odrNumber) => validParticipants.has(odrNumber) && odrNumber !== participantToKeep)
          : [];

        if (existingSelections.length) {
          nextSelections[group.groupId] = existingSelections;
          return;
        }

        nextSelections[group.groupId] = buildDefaultMergeTargets(group, participantToKeep);
      });

      return nextSelections;
    });
  }, [groups, keepSelections]);

  const selectedGroupList = useMemo(() => {
    return groups.filter((group) => selectedGroups[group.groupId]);
  }, [groups, selectedGroups]);

  const canGoBack = page > 1;
  const canGoNext = page < totalPages;

  const handleFilterChange = (field: keyof typeof DEFAULT_FILTERS, value: any) => {
    setFilters((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const toggleMatchField = (field: string, checked: boolean) => {
    setFilters((previous) => {
      const nextMatchOn = checked
        ? Array.from(new Set([...previous.matchOn, field]))
        : previous.matchOn.filter((item) => item !== field);

      return {
        ...previous,
        matchOn: nextMatchOn,
      };
    });
  };

  const handleSearch = async () => {
    if (!filters.matchOn.length) {
      toast.error('Select at least one match field');
      return;
    }

    setPage(1);
    setSelectedGroups({});
    setExpandedGroups({});
    setSubmittedFilters(filters);
    setFiltersExpanded(hasExpandedFilterValues(filters));
    const saveResult = await updateParticipantMergeSearchSettings(filters);
    if (!saveResult.success) {
      toast.error(saveResult.error || 'Failed to save merge search settings');
    }
  };

  const handlePageSizeChange = async (nextPageSize: number) => {
    if (!Number.isFinite(nextPageSize) || nextPageSize <= 0) {
      return;
    }

    setPage(1);
    setFilters((previous) => ({
      ...previous,
      pageSize: nextPageSize,
    }));
    setSubmittedFilters((previous) => ({
      ...previous,
      pageSize: nextPageSize,
    }));

    const saveResult = await updateParticipantMergeSearchSettings({
      ...submittedFilters,
      pageSize: nextPageSize,
    });

    if (!saveResult.success) {
      toast.error(saveResult.error || 'Failed to save merge search settings');
    }
  };

  const handleToggleGroup = (groupId: string, checked: boolean) => {
    setSelectedGroups((previous) => ({
      ...previous,
      [groupId]: checked,
    }));
  };

  const handleSelectVisible = () => {
    const nextSelections: Record<string, boolean> = {};
    groups.forEach((group) => {
      nextSelections[group.groupId] = true;
    });
    setSelectedGroups(nextSelections);
  };

  const handleClearSelection = () => {
    setSelectedGroups({});
  };

  const handleKeepSelectionChange = (group: MergeGroup, odrNumber: string) => {
    const previousKeep = keepSelections[group.groupId] || group.recommendedSurvivor;

    setKeepSelections((previous) => ({
      ...previous,
      [group.groupId]: odrNumber,
    }));

    setMergeSelections((previous) => {
      const existingSelections = Array.isArray(previous[group.groupId])
        ? previous[group.groupId]
        : buildDefaultMergeTargets(group, previousKeep);
      const nextSelectionSet = new Set(existingSelections.filter((value) => value !== odrNumber));

      if (previousKeep && previousKeep !== odrNumber) {
        nextSelectionSet.add(previousKeep);
      }

      return {
        ...previous,
        [group.groupId]: Array.from(nextSelectionSet),
      };
    });
  };

  const handleMergeTargetToggle = (groupId: string, participantId: string, checked: boolean) => {
    setMergeSelections((previous) => {
      const nextSelectionSet = new Set(previous[groupId] || []);

      if (checked) {
        nextSelectionSet.add(participantId);
      }

      if (!checked) {
        nextSelectionSet.delete(participantId);
      }

      return {
        ...previous,
        [groupId]: Array.from(nextSelectionSet),
      };
    });
  };

  const handleMergeSelected = async () => {
    if (!selectedGroupList.length) {
      toast.error('Select at least one match to merge');
      return;
    }

    const selections = selectedGroupList
      .map((group) => {
        const participantToKeep = keepSelections[group.groupId] || group.recommendedSurvivor;
        const participantsToMerge = (mergeSelections[group.groupId] || buildDefaultMergeTargets(group, participantToKeep))
          .filter((odrNumber) => odrNumber !== participantToKeep);

        return {
          participantToKeep,
          participantsToMerge,
        };
      })
      .filter((selection) => selection.participantsToMerge.length > 0);

    if (!selections.length) {
      toast.error('Each selected match needs at least one merge target');
      return;
    }

    try {
      const result = await mergeParticipantGroups({ variables: { selections } });
      const summary = result.data?.mergeParticipantGroups;
      if (!summary) {
        toast.error('Merge completed with no summary');
        return;
      }

      if (summary.totalFailed > 0) {
        const failedMessages = Array.isArray(summary.results)
          ? summary.results
            .filter((entry: any) => Number(entry?.responseCode) >= 400)
            .map((entry: any) => String(entry?.message || '').trim())
            .filter(Boolean)
          : [];
        const primaryFailureMessage = failedMessages[0] || 'Merge failed';
        toast.error(`${primaryFailureMessage}${summary.totalFailed > 1 ? ` (${summary.totalFailed} failed)` : ''}`);
      }

      if (summary.totalFailed === 0) {
        toast.success(`Merged ${summary.totalSucceeded} group(s)`);
      }

      setSelectedGroups({});
      await refetch();
    } catch (mutationError: any) {
      toast.error(mutationError?.message || 'Failed to merge selected groups');
    }
  };

  return (
    <div className="space-y-4 p-4 md:p-6">
      <div className="app-page-shell overflow-hidden">
        <Collapsible open={filtersExpanded} onOpenChange={setFiltersExpanded}>
          <div className="border-b border-[var(--neutral-gray-30)] bg-[var(--surface-muted)] px-4 py-2.5">
            <div className="flex flex-col gap-2 xl:flex-row xl:items-center xl:justify-between">
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="mr-1 text-xs font-semibold uppercase tracking-wide text-[var(--neutral-gray-100)]">Match On</span>
                {MATCH_FIELD_OPTIONS.map((option) => (
                  <label
                    key={option.value}
                    className="flex items-center gap-2 rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-30)] bg-white px-2 py-1 text-xs text-[var(--neutral-gray-120)]"
                  >
                    <Checkbox
                      checked={filters.matchOn.includes(option.value)}
                      onCheckedChange={(checked) => toggleMatchField(option.value, Boolean(checked))}
                    />
                    <span>{option.label}</span>
                  </label>
                ))}
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <CollapsibleTrigger asChild>
                  <Button size="sm" variant="outline">
                    {filtersExpanded ? 'Hide Filters' : 'More Filters'}
                  </Button>
                </CollapsibleTrigger>
                <Button size="sm" onClick={handleSearch} className="flex items-center gap-2">
                  <Search className="h-4 w-4" />
                  Search
                </Button>
              </div>
            </div>
          </div>

          <CollapsibleContent>
            <div className="border-t border-[var(--neutral-gray-30)] px-4 py-3">
              <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-4">
                <div className="xl:col-span-2">
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[var(--neutral-gray-100)]">Name</label>
                  <Input
                    aria-label="Name"
                    className="h-8"
                    value={filters.name}
                    onChange={(event) => handleFilterChange('name', event.target.value)}
                    placeholder="Canonical name"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[var(--neutral-gray-100)]">DOB</label>
                  <Input
                    aria-label="DOB"
                    className="h-8"
                    type="date"
                    value={filters.dob}
                    onChange={(event) => handleFilterChange('dob', event.target.value)}
                  />
                </div>

                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[var(--neutral-gray-100)]">SSN</label>
                  <Input
                    aria-label="SSN"
                    className="h-8"
                    value={filters.ssn}
                    onChange={(event) => handleFilterChange('ssn', event.target.value)}
                    placeholder="Digits or partial"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[var(--neutral-gray-100)]">CIN</label>
                  <Input
                    aria-label="CIN"
                    className="h-8"
                    value={filters.cin}
                    onChange={(event) => handleFilterChange('cin', event.target.value)}
                    placeholder="Letters, numbers, or partial"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[var(--neutral-gray-100)]">CID</label>
                  <Input
                    aria-label="CID"
                    className="h-8"
                    value={filters.cid}
                    onChange={(event) => handleFilterChange('cid', event.target.value)}
                    placeholder="Digits or partial"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[var(--neutral-gray-100)]">ChampID</label>
                  <Input
                    aria-label="ChampID"
                    className="h-8"
                    value={filters.champid}
                    onChange={(event) => handleFilterChange('champid', event.target.value)}
                    placeholder="Digits or partial"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[var(--neutral-gray-100)]">FIN</label>
                  <Input
                    aria-label="FIN"
                    className="h-8"
                    value={filters.fin}
                    onChange={(event) => handleFilterChange('fin', event.target.value)}
                    placeholder="FIN or partial"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[var(--neutral-gray-100)]">Booking</label>
                  <Input
                    aria-label="Booking"
                    className="h-8"
                    value={filters.booking}
                    onChange={(event) => handleFilterChange('booking', event.target.value)}
                    placeholder="Any booking number"
                  />
                </div>

                <div className="flex items-end">
                  <div className="flex h-8 w-full items-center justify-between rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-30)] bg-white px-3">
                    <span className="text-sm text-[var(--neutral-gray-120)]">Full Name Search</span>
                    <Switch
                      aria-label="Full Name Search"
                      checked={filters.fullNameSearch}
                      onCheckedChange={(checked) => handleFilterChange('fullNameSearch', Boolean(checked))}
                    />
                  </div>
                </div>
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>
      </div>

      {error && (
        <Alert>
          <AlertDescription>Failed to load merge candidates: {error.message}</AlertDescription>
        </Alert>
      )}

      <div className="app-page-shell overflow-hidden">
        <div className="border-b border-[var(--neutral-gray-30)] bg-[var(--surface-muted)] px-4 py-3">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
            <div>
              <div className="app-kicker mb-1">Results</div>
              <h2 className="text-xl font-semibold text-[var(--neutral-gray-120)]">Match Results</h2>
              <p className="text-sm text-[var(--neutral-gray-100)]">
                Showing {visibleStart} to {visibleEnd} of {totalCount} matches.
              </p>
            </div>

            <div className="flex flex-wrap items-center justify-end gap-2">
              <div className="flex items-center gap-1">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 w-8 p-0"
                  disabled={!canGoBack}
                  onClick={() => {
                    if (!canGoBack) {
                      return;
                    }

                    setPage((current) => current - 1);
                  }}
                  aria-label="Previous page"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="min-w-[80px] text-center text-xs text-[var(--neutral-gray-100)]">
                  {Math.min(page, totalPages)} / {totalPages}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 w-8 p-0"
                  disabled={!canGoNext}
                  onClick={() => {
                    if (!canGoNext) {
                      return;
                    }

                    setPage((current) => current + 1);
                  }}
                  aria-label="Next page"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>

              <select
                value={String(pageSize)}
                onChange={(event) => handlePageSizeChange(Number(event.target.value))}
                className="h-8 w-[90px] rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-60)] bg-white px-2 text-xs text-[var(--neutral-gray-120)]"
                aria-label="Items per page"
              >
                {PAGE_SIZE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}/page
                  </option>
                ))}
              </select>

              <Button size="sm" variant="outline" onClick={handleSelectVisible}>Select Visible</Button>
              <Button size="sm" variant="outline" onClick={handleClearSelection}>Clear Selection</Button>
              <Button size="sm" onClick={handleMergeSelected} disabled={merging || !selectedGroupList.length} className="flex items-center gap-2">
                <GitMerge className="h-4 w-4" />
                {merging ? 'Merging...' : 'Merge Selected'}
              </Button>
            </div>
          </div>
        </div>

        <div className="divide-y divide-[var(--neutral-gray-30)]">
          {!loading && !groups.length && (
            <div className="px-4 py-10 text-center text-sm text-[var(--neutral-gray-100)]">
              No matches found for the current search.
            </div>
          )}

          {groups.map((group) => {
            const colorClasses = getColorClasses(group.scoreColor);
            const participantToKeep = keepSelections[group.groupId] || group.recommendedSurvivor;
            const mergeTargets = mergeSelections[group.groupId] || buildDefaultMergeTargets(group, participantToKeep);
            const isExpanded = Boolean(expandedGroups[group.groupId]);

            return (
              <Collapsible
                key={group.groupId}
                open={isExpanded}
                onOpenChange={(open) => {
                  setExpandedGroups((previous) => ({
                    ...previous,
                    [group.groupId]: open,
                  }));
                }}
              >
                <div className="px-4 py-3">
                  <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
                    <div className="flex min-w-0 items-start gap-3">
                      <div className="pt-1">
                        <Checkbox
                          aria-label={`Select match group ${group.groupId}`}
                          checked={Boolean(selectedGroups[group.groupId])}
                          onCheckedChange={(checked) => handleToggleGroup(group.groupId, Boolean(checked))}
                        />
                      </div>

                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="text-base font-semibold text-[var(--neutral-gray-120)]">
                            {group.participantCount} participant match
                          </div>
                          <Badge variant="outline">Keep {participantToKeep}</Badge>
                          <Badge variant="outline">{mergeTargets.length} target(s)</Badge>
                        </div>

                        <p className="mt-1 text-sm text-[var(--neutral-gray-100)]">
                          Recommended survivor {group.recommendedSurvivor}. Matched on {group.matchedFields.slice(0, 3).map((field) => field.label).join(', ') || 'review'}.
                        </p>

                        <div className="mt-2 flex flex-wrap gap-2">
                          {group.matchedFields.slice(0, 3).map((field) => (
                            <Badge key={`${group.groupId}-${field.field}-${field.value}`} variant="outline" className={cn('max-w-full truncate', colorClasses.badge)}>
                              {field.label}: {field.value}
                            </Badge>
                          ))}
                          {group.matchedFields.length > 3 && (
                            <Badge variant="outline">+{group.matchedFields.length - 3} more</Badge>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex min-w-[7rem] self-stretch flex-col items-end justify-between gap-2">
                      <ScorePill percent={group.matchPercent} scoreColor={group.scoreColor} label={group.safetyLabel} compact />
                      <CollapsibleTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="!h-auto !border-0 !bg-transparent !px-0 !py-0 !text-xs !font-medium !leading-4 !text-[var(--azure-primary)] !shadow-none hover:!bg-transparent hover:underline hover:underline-offset-2"
                        >
                          {isExpanded ? 'Collapse' : 'Expand'}
                          <ChevronDown className={cn('ml-1 h-4 w-4 transition-transform', isExpanded && 'rotate-180')} />
                        </Button>
                      </CollapsibleTrigger>
                    </div>
                  </div>

                  <CollapsibleContent>
                    <div className="mt-4 space-y-3">
                      {group.matchPercent < 60 && (
                        <div className={cn('flex items-start gap-2 rounded-[var(--border-radius-large)] border px-3 py-2.5 text-sm', colorClasses.panel)}>
                          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
                          <span>Low-confidence match. Review the survivor and merge targets carefully.</span>
                        </div>
                      )}

                      <div className="overflow-hidden rounded-[var(--border-radius-large)] border border-[var(--neutral-gray-30)] bg-white">
                        <div className="border-b border-[var(--neutral-gray-30)] bg-[var(--surface-muted)] px-4 py-2.5">
                          <h3 className="text-sm font-semibold text-[var(--neutral-gray-120)]">Participants</h3>
                          <p className="mt-1 text-sm text-[var(--neutral-gray-100)]">
                            Keep one participant, then check which records should merge into it. Matched values are highlighted below.
                          </p>
                        </div>

                        <div className="overflow-x-auto">
                          <RadioGroup
                            value={participantToKeep}
                            onValueChange={(value) => handleKeepSelectionChange(group, value)}
                          >
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Keep</TableHead>
                                  <TableHead>Merge</TableHead>
                                  <TableHead>ODR</TableHead>
                                  <TableHead>Name</TableHead>
                                  <TableHead>DOB</TableHead>
                                  <TableHead>SSN</TableHead>
                                  <TableHead>CIN</TableHead>
                                  <TableHead>CID / ChampID</TableHead>
                                  <TableHead>FIN</TableHead>
                                  <TableHead>Bookings</TableHead>
                                  <TableHead>Score</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {group.participants.map((participant) => {
                                  const participantClasses = getColorClasses(participant.scoreColor);
                                  const isSurvivor = participant.odrNumber === participantToKeep;
                                  const isMergeTarget = mergeTargets.includes(participant.odrNumber);

                                  return (
                                    <TableRow
                                      key={`${group.groupId}-${participant.odrNumber}`}
                                      className={cn(
                                        isSurvivor && 'bg-[var(--surface-muted)]/50',
                                        group.recommendedSurvivor === participant.odrNumber && 'border-l-4 border-l-green-400'
                                      )}
                                    >
                                      <TableCell>
                                        <div className="flex items-center gap-2">
                                          <RadioGroupItem aria-label={`Keep participant ${participant.odrNumber}`} value={participant.odrNumber} />
                                          {participant.recommendedKeep && <CheckCircle2 className="h-4 w-4 text-green-600" />}
                                        </div>
                                      </TableCell>
                                      <TableCell>
                                        <Checkbox
                                          aria-label={`Merge participant ${participant.odrNumber}`}
                                          checked={isSurvivor ? false : isMergeTarget}
                                          disabled={isSurvivor}
                                          onCheckedChange={(checked) => handleMergeTargetToggle(group.groupId, participant.odrNumber, Boolean(checked))}
                                        />
                                      </TableCell>
                                      <TableCell className="font-medium">
                                        <Link
                                          to={`/participants/${participant.odrNumber}`}
                                          className="text-[var(--azure-primary)] hover:underline"
                                        >
                                          {participant.odrNumber}
                                        </Link>
                                      </TableCell>
                                      <TableCell className={getMatchedCellClassName(getMatchHighlightState(participant, group.participants, 'name'))}>
                                        <div>{getParticipantName(participant)}</div>
                                        <div className="mt-1 text-xs text-[var(--neutral-gray-100)]">
                                          Completeness: {participant.completeness}
                                        </div>
                                      </TableCell>
                                      <TableCell className={getMatchedCellClassName(getMatchHighlightState(participant, group.participants, 'dob'))}>{participant.dob || '—'}</TableCell>
                                      <TableCell className={getMatchedCellClassName(getMatchHighlightState(participant, group.participants, 'ssn'))}>{participant.ssn || '—'}</TableCell>
                                      <TableCell className={getMatchedCellClassName(getMatchHighlightState(participant, group.participants, 'cin'))}>{participant.cin || '—'}</TableCell>
                                      <TableCell className={getMatchedCellClassName(getMatchHighlightState(participant, group.participants, ['cid', 'champid']))}>{participant.cid || participant.champid || '—'}</TableCell>
                                      <TableCell className={getMatchedCellClassName(getMatchHighlightState(participant, group.participants, 'fin'))}>{participant.fin || '—'}</TableCell>
                                      <TableCell className={getMatchedCellClassName(getMatchHighlightState(participant, group.participants, 'booking'))}>
                                        <div className="max-w-[16rem] truncate text-sm text-[var(--neutral-gray-120)]">
                                          {participant.bookings.length ? participant.bookings.join(', ') : '—'}
                                        </div>
                                      </TableCell>
                                      <TableCell>
                                        <div className="min-w-[11rem]">
                                          <Badge variant="outline" className={cn('mb-2', participantClasses.badge)}>
                                            {participant.matchPercent}%
                                          </Badge>
                                          <div className="h-2 w-full overflow-hidden rounded-full bg-[var(--neutral-gray-20)]">
                                            <div className={cn('h-full rounded-full transition-all', participantClasses.fill)} style={{ width: `${participant.matchPercent}%` }} />
                                          </div>
                                        </div>
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </RadioGroup>
                        </div>
                      </div>
                    </div>
                  </CollapsibleContent>
                </div>
              </Collapsible>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ParticipantMergePage;
